
import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tabs,
    Tab,
    Box,
    IconButton,
    CircularProgress,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { CameraAlt, CloudUpload, Close } from '@mui/icons-material';
import { apiMultiPart } from '../../services/api';
import { compressImage } from '../../services/utils/compressImage';

const ProfilePictureModal = ({ open, onClose, onSave }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState(0);
    const [mediaStream, setMediaStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const videoRef = useRef(null);
    const fileInputRef = useRef(null);

    // Camera handling
    const startCamera = async () => {
        try {
            setCameraError(null);
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'user',
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });
            setMediaStream(stream);

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play().catch(err => {
                        console.error('Error playing video:', err);
                    });
                };
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            setCameraError('Could not access camera. Please check permissions.');
            setMediaStream(null);
        }
    };

    const stopCamera = () => {
        if (mediaStream) {
            mediaStream.getTracks().forEach(track => track.stop());
            setMediaStream(null);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current) return;

        const video = videoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');

        // Flip horizontally for mirror effect
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = canvas.toDataURL('image/jpeg', 0.8);
        setCapturedImage(imageData);
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setSelectedFile(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle camera when modal opens/closes or tabs change
    useEffect(() => {
        if (open && activeTab === 0) {
            startCamera();
        } else {
            stopCamera();
        }

        return () => {
            stopCamera();
        };
    }, [open, activeTab]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setCapturedImage(null);
            setSelectedFile(null);
            setActiveTab(0);
            setCameraError(null);
        }
    }, [open]);

    const handleSave = async () => {
        setLoading(true);
        try {
            const imageData = activeTab === 0 ? capturedImage : selectedFile;
            if (!imageData) return;

            // Convert to Blob regardless of source
            const response = await fetch(imageData);
            const blob = await response.blob();
            
            // Compress the image
            const compressedBlob = await compressImage(blob);
            
            // Upload to server
            const formData = new FormData();
            formData.append('avatar', compressedBlob, 'profile.jpg');

            await apiMultiPart.post(
                '/v1/api/users/user/upload-profile-picture',
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                    }
                }
            );

            // Get updated profile picture and pass to parent
            const responseImage = await apiMultiPart.get('/v1/api/users/user/profile-image');
            const imageUrl = responseImage.data.url;
            onSave(imageUrl);
            
            onClose();
        } catch (error) {
            console.error('Error saving profile picture:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isSmallScreen}>
            <DialogTitle
                sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                Update Profile Picture
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: 'primary.contrastText',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="fullWidth"
                >
                    <Tab label="Take Photo" icon={<CameraAlt />} />
                    <Tab label="Upload Photo" icon={<CloudUpload />} />
                </Tabs>

                <Box sx={{ mt: 2, height: 400, position: 'relative' }}>
                    {activeTab === 0 ? (
                        <>
                            {cameraError ? (
                                <Box sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    p: 2,
                                    backgroundColor: '#f5f5f5'
                                }}>
                                    <Typography color="error" sx={{ mb: 2 }}>
                                        {cameraError}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={startCamera}
                                    >
                                        Retry Camera
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        muted
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            backgroundColor: '#000',
                                            transform: 'scaleX(-1)',
                                            display: capturedImage ? 'none' : 'block'
                                        }}
                                    />

                                    {capturedImage && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: '#000'
                                        }}>
                                            <img
                                                src={capturedImage}
                                                alt="Captured"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain',
                                                    transform: 'scaleX(-1)'
                                                }}
                                            />
                                            <Box sx={{
                                                position: 'absolute',
                                                bottom: 16,
                                                left: 0,
                                                right: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                gap: 2
                                            }}>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={() => setCapturedImage(null)}
                                                >
                                                    Retake
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={handleSave}
                                                    disabled={loading}
                                                >
                                                    {loading ? <CircularProgress size={24} /> : 'Use This Photo'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    {!capturedImage && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={capturePhoto}
                                            sx={{
                                                position: 'absolute',
                                                bottom: 16,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                zIndex: 1
                                            }}
                                        >
                                            Capture
                                        </Button>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <Box sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px dashed',
                            borderRadius: 1,
                            p: 2,
                            backgroundColor: '#f5f5f5'
                        }}>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileUpload}
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CloudUpload />}
                                onClick={() => fileInputRef.current.click()}
                                sx={{ mb: 2 }}
                            >
                                Choose File
                            </Button>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                or drag and drop an image here
                            </Typography>
                            
                            {selectedFile && (
                                <>
                                    <Box sx={{
                                        width: '100%',
                                        height: 300,
                                        overflow: 'hidden',
                                        mt: 2
                                    }}>
                                        <img
                                            src={selectedFile}
                                            alt="Preview"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Box>
                                    <DialogActions sx={{ width: '100%', justifyContent: 'center' }}>
                                        <Button onClick={() => setSelectedFile(null)}>
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleSave}
                                            variant="contained"
                                            color="primary"
                                            disabled={loading}
                                        >
                                            {loading ? <CircularProgress size={24} /> : 'Save'}
                                        </Button>
                                    </DialogActions>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ProfilePictureModal;